/**
 * useApplyDocumentSlots — the document half of the `/apply` bound-control adapter
 * (apply-document-uploads spec, task 5.1).
 *
 * The renderer's `DOCUMENT_SLOT` bound control (`DocumentSlotBoundControl` /
 * `DocumentSlotRow`) reads its slots and uploads from an injected
 * {@link BoundControlAdapter}, but on `/apply` that adapter is passed empty, so
 * nothing an applicant could upload would ever satisfy the submit gate. This
 * hook closes that gap: it loads the per-competition `Document_Catalog` and the
 * caller's own uploaded `DocumentReference`s, and produces the
 * `documentCatalog` / `applicantType` / `uploadedDocuments` read-models plus the
 * `onUploadDocument` / `onRemoveDocument` write callbacks the control consumes.
 *
 * The upload path REUSES the same backend the Document Center uses — the
 * `getDocumentUploadUrl` presign path, a direct browser `PUT` to the shared S3
 * bucket, and the `createDocumentReference` store — linking each upload to its
 * slot via `catalogEntryId`, and it never re-implements a validation decision:
 * the pre-upload file check is the SAME pure `validateDocumentUpload` the catalog
 * declares its constraints through, so a file rejected here is exactly a file the
 * catalog would reject (apply-document-uploads Req 5).
 *
 * `onUploadDocument` runs, per file:
 *   1. `validateDocumentUpload(entry, file, currentCountForSlot)` (pure) — reject
 *      BEFORE any network call on a constraint miss (Req 5.1–5.4, 6.4).
 *   2. `getDocumentUploadUrl` — a presigned, slot-scoped S3 PUT URL (Req 2.1).
 *   3. a direct `PUT` of the file bytes to the presigned URL (Req 2.2).
 *   4. `createDocumentReference` carrying the slot's `catalogEntryId` (Req 2.3, 2.5).
 *   5. a refresh of `listMyUploadedDocuments` so the slot reflects the upload
 *      without a full-page reload (Req 2.6).
 *
 * A per-slot concurrency lock (tracked in-flight slot ids) prevents a second
 * concurrent upload for the same slot (Req 2.7). Every failure stage throws a
 * {@link DocumentUploadError} naming the affected slot so the caller can surface a
 * per-slot, retryable message while retaining the slot's previously uploaded
 * references (Req 9). Ownership is enforced by the transport: `applicantId` is
 * NEVER a method argument — the live client derives it from the authenticated
 * caller (Req 10.2, 10.3).
 *
 * Validates: apply-document-uploads Requirements 2.1, 2.2, 2.3, 2.6, 2.7, 6.1,
 *            6.2, 7.1, 7.2, 7.3
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { applicationFormClient } from '@/lib/application-form/client';
import type { DocumentCatalogEntry, DocumentRef } from '@/lib/application-form/client';
import {
  validateDocumentUpload,
  type ApplicantType,
  type DocumentCatalogEntry as CatalogSlot,
} from '@/components/application-form/renderer/pure';

/**
 * A per-slot upload failure. Carries the affected slot's `catalogEntryId` and,
 * for a validation rejection, the pure constraint `code`, so the caller can
 * surface a slot-scoped, retryable message (Req 9.1–9.3) without guessing which
 * row failed.
 */
export class DocumentUploadError extends Error {
  /** The catalog slot the failed upload targeted. */
  readonly catalogEntryId: string;
  /** The pure validation code when the failure is a pre-upload constraint miss. */
  readonly code?: string;

  constructor(message: string, catalogEntryId: string, code?: string) {
    super(message);
    this.name = 'DocumentUploadError';
    this.catalogEntryId = catalogEntryId;
    this.code = code;
  }
}

/** The document half of the `/apply` {@link BoundControlAdapter}, plus load/lock state. */
export interface UseApplyDocumentSlotsResult {
  /** The director-configured per-competition Document_Catalog (Req 1.1, 1.2). */
  documentCatalog: DocumentCatalogEntry[];
  /** The applicant's type, for the catalog's applicant-type scope. */
  applicantType: ApplicantType | undefined;
  /** The caller's own uploaded documents, matched to slots by `catalogEntryId` (Req 7.1, 7.2). */
  uploadedDocuments: DocumentRef[];
  /** Upload picked files for a slot (validate → presign → PUT → create → refresh). */
  onUploadDocument: (entry: CatalogSlot, files: FileList) => Promise<DocumentRef[]>;
  /** Remove one uploaded document (delete → refresh) (Req 6.1, 6.2). */
  onRemoveDocument: (ref: DocumentRef) => Promise<void>;
  /** The slots with an upload currently in flight (the per-slot concurrency lock; Req 2.7). */
  uploadingSlots: ReadonlySet<string>;
  /** True while the initial catalog + uploads load is in progress. */
  loading: boolean;
  /** A load-failure message, or null. */
  error: string | null;
}

function errorMessage(err: unknown, fallback: string): string {
  const errors = (err as { errors?: Array<{ message?: string }> })?.errors;
  if (errors && errors.length > 0 && errors[0]?.message) return errors[0].message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/**
 * Load the `/apply` document slots and provide the upload/remove callbacks the
 * `DOCUMENT_SLOT` bound control consumes.
 *
 * @param competitionId  the competition whose Document_Catalog is loaded (Req 1.1).
 * @param competitionYear the applicant's competition year — scopes the caller's
 *                        own uploaded documents (Req 7.1).
 * @param applicantType  the applicant's type, for the catalog's applicant-type
 *                        scope; `undefined` until participation resolves.
 */
export function useApplyDocumentSlots(
  competitionId: string,
  competitionYear: number,
  applicantType: ApplicantType | undefined,
): UseApplyDocumentSlotsResult {
  const [documentCatalog, setDocumentCatalog] = useState<DocumentCatalogEntry[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<DocumentRef[]>([]);
  const [uploadingSlots, setUploadingSlots] = useState<ReadonlySet<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The latest uploaded documents, read inside the stable upload/remove
  // callbacks so the per-slot count for `maxFileCount` never goes stale.
  const uploadedRef = useRef<DocumentRef[]>([]);
  // The in-flight slot ids — the source of truth for the per-slot concurrency
  // lock (Req 2.7). A ref so a second synchronous call sees the first's mark.
  const inFlightRef = useRef<Set<string>>(new Set());

  const applyUploaded = useCallback((docs: DocumentRef[]) => {
    uploadedRef.current = docs;
    setUploadedDocuments(docs);
  }, []);

  /** Re-load the caller's own uploaded documents so a slot reflects the latest server state. */
  const refresh = useCallback(async () => {
    const docs = await applicationFormClient.listMyUploadedDocuments(competitionYear);
    applyUploaded(docs);
  }, [competitionYear, applyUploaded]);

  // --- Load (Req 7.1, 7.2, 7.3) --------------------------------------------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const [catalog, docs] = await Promise.all([
          applicationFormClient.getDocumentCatalog(competitionId),
          applicationFormClient.listMyUploadedDocuments(competitionYear),
        ]);
        if (cancelled) return;
        setDocumentCatalog(catalog);
        applyUploaded(docs);
      } catch (err) {
        if (cancelled) return;
        setError(errorMessage(err, 'We couldn’t load your documents. Please try again.'));
        setDocumentCatalog([]);
        applyUploaded([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [competitionId, competitionYear, applyUploaded]);

  /** Upload one file through the shared presign → PUT → createDocumentReference path. */
  const uploadOne = useCallback(
    async (entry: CatalogSlot, file: File, existingCountForSlot: number): Promise<DocumentRef> => {
      // 1 — pure constraint check; reject BEFORE any network call (Req 5, 6.4).
      const violation = validateDocumentUpload(
        entry,
        { contentType: file.type, sizeBytes: file.size },
        existingCountForSlot,
      );
      if (violation) {
        throw new DocumentUploadError(violation.message, entry.id, violation.code);
      }

      // A known slot keeps its mapped DocumentType; an untagged slot uploads as
      // the generic APPLICATION_DOCUMENT (Decision D2). The `catalogEntryId` —
      // not the documentType — is the gate's slot-match key.
      const documentType = entry.documentType ?? 'APPLICATION_DOCUMENT';

      // 2 — presigned, slot-scoped S3 PUT URL (Req 2.1).
      let presigned;
      try {
        presigned = await applicationFormClient.getDocumentUploadUrl({
          competitionYear,
          documentType,
          fileName: file.name,
          contentType: file.type,
          catalogEntryId: entry.id,
        });
      } catch (err) {
        throw new DocumentUploadError(
          errorMessage(err, 'We couldn’t start the upload. Please try again.'),
          entry.id,
        );
      }

      // 3 — direct browser PUT of the file bytes to the shared bucket (Req 2.2).
      // A non-2xx (or a network throw) fails the upload and, crucially, NEVER
      // creates a DocumentReference for the failed upload (Req 9.2).
      let putResponse: Response;
      try {
        putResponse = await fetch(presigned.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
      } catch (err) {
        throw new DocumentUploadError(
          errorMessage(err, 'The file upload failed. Please try again.'),
          entry.id,
        );
      }
      if (!putResponse.ok) {
        throw new DocumentUploadError('The file upload failed. Please try again.', entry.id);
      }

      // 4 — persist the DocumentReference carrying the slot link (Req 2.3, 2.5).
      try {
        return await applicationFormClient.createDocumentReference({
          competitionYear,
          documentType,
          s3Key: presigned.s3Key,
          fileName: file.name,
          contentType: file.type,
          catalogEntryId: entry.id,
        });
      } catch (err) {
        throw new DocumentUploadError(
          errorMessage(err, 'We couldn’t save your uploaded document. Please try again.'),
          entry.id,
        );
      }
    },
    [competitionYear],
  );

  const onUploadDocument = useCallback(
    async (entry: CatalogSlot, files: FileList): Promise<DocumentRef[]> => {
      if (!files || files.length === 0) {
        return [];
      }
      const slotId = entry.id;

      // Per-slot concurrency lock: a second concurrent upload for the same slot
      // is a no-op while the first is in flight (Req 2.7).
      if (inFlightRef.current.has(slotId)) {
        return [];
      }
      inFlightRef.current.add(slotId);
      setUploadingSlots(new Set(inFlightRef.current));

      try {
        const created: DocumentRef[] = [];
        const baseCount = uploadedRef.current.filter(
          (doc) => (doc.catalogEntryId ?? '') === slotId,
        ).length;
        for (const file of Array.from(files)) {
          // Count already-uploaded plus the ones accepted earlier in this batch
          // so `maxFileCount` is enforced across a multi-file selection (Req 5.3).
          const ref = await uploadOne(entry, file, baseCount + created.length);
          created.push(ref);
        }
        // Refresh so the slot reflects the new uploads without a reload (Req 2.6).
        await refresh();
        return created;
      } finally {
        inFlightRef.current.delete(slotId);
        setUploadingSlots(new Set(inFlightRef.current));
      }
    },
    [uploadOne, refresh],
  );

  const onRemoveDocument = useCallback(
    async (ref: DocumentRef): Promise<void> => {
      await applicationFormClient.deleteDocumentReference(ref.id);
      // Refresh so the removed document drops from the slot without a reload (Req 6.2).
      await refresh();
    },
    [refresh],
  );

  return {
    documentCatalog,
    applicantType,
    uploadedDocuments,
    onUploadDocument,
    onRemoveDocument,
    uploadingSlots,
    loading,
    error,
  };
}

export default useApplyDocumentSlots;
